"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CARD_RARITY_VALUES = void 0;
exports.sleep = sleep;
exports.isNonEmptyString = isNonEmptyString;
exports.resolveArtwork = resolveArtwork;
exports.slugifyBase = slugifyBase;
exports.slugify = slugify;
exports.parseCardSearchQuery = parseCardSearchQuery;
exports.cardSearchHint = cardSearchHint;
exports.hasCardSearchConditions = hasCardSearchConditions;
exports.shouldRequestCardSearchQuery = shouldRequestCardSearchQuery;
exports.markdownToPlainText = markdownToPlainText;
const card_number_normalize_1 = require("../card-number-normalize");
const zod_1 = require("../zod");
function sleep(ms) {
    return new Promise((resolve) => setTimeout(() => resolve(), ms));
}
function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
}
function resolveArtwork(url) {
    const src = url?.trim();
    if (src && src.toLowerCase().startsWith('https://')) {
        return { kind: 'image', src };
    }
    return { kind: 'placeholder' };
}
/**
 * Convert an arbitrary string into a kebab-case slug:
 * - lowercase ASCII
 * - non-alnum runs collapse into a single dash
 * - leading/trailing dashes trimmed
 * - capped at 64 chars (matches `slugSchema` in zod/index.ts)
 *
 * Non-ASCII characters (e.g. CJK) are stripped, so titles like "天穹黎明"
 * collapse to an empty base — callers MUST always append the random suffix
 * via `slugify` + uniqueness retry to keep the slug non-empty.
 */
function slugifyBase(input) {
    return input
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 56);
}
const SLUG_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
/**
 * Compose a slug as `<slugifyBase(title)>-<random suffix>`. The suffix length
 * defaults to 6 chars (~36^6 ≈ 2.2B combos), which collides only after
 * ~50k topics under the birthday bound. The forum router additionally
 * retries on Prisma P2002 (unique-constraint violation) before failing.
 *
 * Cross-runtime random: prefers `globalThis.crypto.getRandomValues` when
 * present (browsers, Node 19+, Cloudflare Workers, Tauri WebView). Falls
 * back to `Math.random` so the helper still works in the most stripped-down
 * miniapp jscore variants.
 */
function slugify(input, suffixLength = 6) {
    const base = slugifyBase(input) || 'topic';
    let suffix = '';
    const cryptoLike = globalThis.crypto;
    if (cryptoLike?.getRandomValues) {
        const bytes = new Uint8Array(suffixLength);
        cryptoLike.getRandomValues(bytes);
        for (let i = 0; i < suffixLength; i += 1) {
            suffix += SLUG_ALPHABET[bytes[i] % SLUG_ALPHABET.length];
        }
    }
    else {
        for (let i = 0; i < suffixLength; i += 1) {
            suffix += SLUG_ALPHABET[Math.floor(Math.random() * SLUG_ALPHABET.length)];
        }
    }
    return `${base}-${suffix}`;
}
exports.CARD_RARITY_VALUES = ['common', 'rare', 'epic', 'legendary'];
const SILENT_INCOMPLETE_PREFIXES = new Set(['r', 's', 'c', 'y']);
const RELEASE_YEAR = /^[0-9]{4}$/;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function rememberIgnored(ignored, next) {
    return ignored ?? next;
}
function appendNameToken(groups, token) {
    if (groups.length === 0)
        groups.push([]);
    groups[groups.length - 1].push(token);
}
function parseCardSearchQuery(query) {
    const tokens = query
        .trim()
        .split(/\s+/)
        .filter((token) => token.length > 0);
    let rarity;
    let seriesSlug;
    let cardNumber;
    let releaseYear;
    let ignored;
    let sawCardNumber = false;
    let sawReleaseYear = false;
    const includeGroups = [[]];
    const excludeTerms = [];
    for (const rawToken of tokens) {
        if (rawToken === 'OR') {
            if (includeGroups[includeGroups.length - 1].length > 0) {
                includeGroups.push([]);
            }
            continue;
        }
        const token = rawToken.trim();
        const prefixMatch = token.match(/^([A-Za-z]+):(.+)$/);
        const incompletePrefixMatch = token.match(/^([A-Za-z]+):$/);
        if (incompletePrefixMatch) {
            const prefix = incompletePrefixMatch[1].toLowerCase();
            if (SILENT_INCOMPLETE_PREFIXES.has(prefix))
                continue;
            return { ok: false, reason: 'unknownPrefix', token };
        }
        if (prefixMatch) {
            const prefix = prefixMatch[1].toLowerCase();
            const value = prefixMatch[2];
            if (prefix === 'r') {
                if (!exports.CARD_RARITY_VALUES.some((item) => item === value)) {
                    return { ok: false, reason: 'invalidRarity', token };
                }
                if (!rarity) {
                    const matched = exports.CARD_RARITY_VALUES.find((item) => item === value);
                    if (matched)
                        rarity = matched;
                }
                continue;
            }
            if (prefix === 's') {
                if (!seriesSlug && SLUG_PATTERN.test(value)) {
                    seriesSlug = value;
                }
                continue;
            }
            if (prefix === 'c') {
                if (sawCardNumber)
                    continue;
                const trimmed = value.trim();
                if (trimmed.length === 0)
                    continue;
                sawCardNumber = true;
                if (trimmed.length > zod_1.CARD_NUMBER_MAX) {
                    ignored = rememberIgnored(ignored, { reason: 'cardNumberTooLong', token: rawToken });
                    continue;
                }
                const normalized = (0, card_number_normalize_1.normalizedCardNumber)(trimmed);
                if (normalized)
                    cardNumber = normalized;
                continue;
            }
            if (prefix === 'y') {
                if (sawReleaseYear)
                    continue;
                const trimmed = value.trim();
                if (trimmed.length === 0)
                    continue;
                sawReleaseYear = true;
                if (RELEASE_YEAR.test(trimmed)) {
                    releaseYear = Number(trimmed);
                }
                else {
                    ignored = rememberIgnored(ignored, { reason: 'invalidYear', token: rawToken });
                }
                continue;
            }
            if (/^[A-Za-z]+$/.test(prefix)) {
                return { ok: false, reason: 'unknownPrefix', token };
            }
        }
        if (/^[A-Za-z]+[<>=]/.test(token)) {
            return { ok: false, reason: 'unknownPrefix', token };
        }
        if (token.startsWith('-') && token.length > 1) {
            excludeTerms.push(token.slice(1));
            continue;
        }
        appendNameToken(includeGroups, token);
    }
    return {
        ok: true,
        ...(rarity ? { rarity } : {}),
        ...(seriesSlug ? { seriesSlug } : {}),
        ...(cardNumber ? { cardNumber } : {}),
        ...(releaseYear !== undefined ? { releaseYear } : {}),
        ...(ignored ? { ignored } : {}),
        includeGroups: includeGroups.filter((group) => group.length > 0),
        excludeTerms,
    };
}
/** Hint for the search box. Invalid `c:` / `y:` stay on an otherwise ok parse. */
function cardSearchHint(parsed) {
    if (!parsed.ok)
        return { reason: parsed.reason, token: parsed.token };
    if (parsed.ignored)
        return parsed.ignored;
    return null;
}
function hasCardSearchConditions(parsed) {
    return (Boolean(parsed.rarity) ||
        Boolean(parsed.seriesSlug) ||
        Boolean(parsed.cardNumber) ||
        parsed.releaseYear !== undefined ||
        parsed.includeGroups.length > 0 ||
        parsed.excludeTerms.length > 0);
}
function shouldRequestCardSearchQuery(query) {
    const parsed = parseCardSearchQuery(query);
    return parsed.ok && hasCardSearchConditions(parsed);
}
function markdownToPlainText(markdown, maxLength) {
    if (maxLength <= 0)
        return '';
    const source = Array.from(markdown).slice(0, 2000).join('');
    const lines = [];
    let fence = null;
    for (const line of source.split(/\r?\n/)) {
        const trimmed = line.trimStart();
        if (fence) {
            if (trimmed.startsWith(fence))
                fence = null;
            continue;
        }
        if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
            fence = trimmed.startsWith('```') ? '```' : '~~~';
            continue;
        }
        if (/^(?: {4}|\t)/.test(line))
            continue;
        if (/^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*$/.test(line))
            continue;
        lines.push(line);
    }
    const plain = lines
        .join('\n')
        // Images and raw HTML do not contribute to forum list summaries.
        .replace(/!\[[^\]\n]{0,200}]\([^)\n]{0,500}\)/g, ' ')
        .replace(/<((?:https?:\/\/|mailto:)[^>\s]{0,500})>/g, '$1')
        .replace(/<[A-Za-z][A-Za-z0-9-]*(?:\s[^>\n]{0,200})?>[^<\n]{0,1000}<\/[A-Za-z][A-Za-z0-9-]*>/g, ' ')
        .replace(/<\/?[A-Za-z][^>\n]{0,200}>/g, ' ')
        // Keep useful inline text while stripping Markdown delimiters.
        .replace(/\[([^\]\n]{1,500})]\([^)\n]{0,500}\)/g, '$1')
        .replace(/`([^`\n]{1,500})`/g, '$1')
        .replace(/^\s{0,3}#{1,6}\s+/gm, '')
        .replace(/^\s{0,3}>\s?/gm, '')
        .replace(/(^|\s)[-*+]\s+\[[ xX]\]\s+/g, ' ')
        .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+/gm, '')
        .replace(/\[[ xX]\]\s+/g, '')
        .replace(/\*\*([^*\n]+)\*\*/g, '$1')
        .replace(/__([^_\n]+)__/g, '$1')
        .replace(/(^|[\s([{"'])\*([^*\s][^*\n]*?)\*(?=$|[\s)\]}.,!?;:'"])/g, '$1$2')
        .replace(/(^|[\s([{"'])_([^_\s][^_\n]*?)_(?=$|[\s)\]}.,!?;:'"])/g, '$1$2')
        .replace(/~~([^~\n]+)~~/g, '$1')
        .replace(/\|/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!plain)
        return '';
    const chars = Array.from(plain);
    if (chars.length <= maxLength)
        return plain;
    return `${chars.slice(0, maxLength).join('').trimEnd()}…`;
}
//# sourceMappingURL=index.js.map